/* AdminAnalytics — comprehensive dashboard covering:
 *   (a) Traffic & reading
 *   (b) Users
 *   (c) Content management
 *   (d) Push notifications
 *   (e) Ads
 *   (f) System health
 *
 * All charts powered by recharts; data fetched from /api/analytics/*.
 */
import React, { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import {
  Activity, Users as UsersI, Newspaper, BellRing, Megaphone, ServerCog,
  TrendingUp, Eye, MapPin, Inbox, AlertTriangle, CheckCircle2, Mail,
  HardDrive, Clock, RefreshCw, CalendarRange, ArrowUpRight, ArrowDownRight, Trophy,
} from "lucide-react";
import {
  ResponsiveContainer, LineChart, Line, AreaChart, Area, BarChart, Bar,
  XAxis, YAxis, Tooltip, CartesianGrid, PieChart, Pie, Cell, Legend,
} from "recharts";
import { AdminPageHeader } from "./_helpers";

const TABS = [
  { id: "overview", label: "Genel Bakış", icon: Activity },
  { id: "traffic", label: "Trafik & Okuma", icon: TrendingUp },
  { id: "users", label: "Kullanıcılar", icon: UsersI },
  { id: "content", label: "İçerik", icon: Newspaper },
  { id: "columnists", label: "Köşe Yazarları", icon: Newspaper },
  { id: "push", label: "Push", icon: BellRing },
  { id: "ads", label: "Reklam", icon: Megaphone },
  { id: "yearly", label: "Yıllık Rapor", icon: CalendarRange },
  { id: "system", label: "Sistem", icon: ServerCog },
];

const COLORS = ["#e11d2a", "#1e40af", "#16a34a", "#f59e0b", "#9333ea", "#0891b2", "#db2777", "#65a30d"];

const trDate = (s) => {
  try { return new Date(s).toLocaleDateString("tr-TR", { day: "2-digit", month: "short" }); }
  catch { return s; }
};
const trDateTime = (s) => {
  try { return new Date(s).toLocaleString("tr-TR"); }
  catch { return s; }
};
const fmtBytes = (b) => {
  if (!b) return "0 B";
  const u = ["B", "KB", "MB", "GB", "TB"];
  let i = 0; let n = b;
  while (n >= 1024 && i < u.length - 1) { n /= 1024; i++; }
  return `${n.toFixed(1)} ${u[i]}`;
};

export default function AdminAnalytics() {
  const [tab, setTab] = useState("overview");
  const [days, setDays] = useState(30);

  return (
    <div data-testid="admin-analytics">
      <AdminPageHeader
        title="Analitik"
        subtitle="Trafik, kullanıcı, içerik ve sistem performansı tek panelde."
        action={
          <select
            value={days}
            onChange={(e) => setDays(parseInt(e.target.value, 10))}
            className="text-sm px-3 py-1.5 border border-gray-200 rounded-md"
            data-testid="analytics-days"
          >
            <option value={7}>Son 7 gün</option>
            <option value={14}>Son 14 gün</option>
            <option value={30}>Son 30 gün</option>
            <option value={90}>Son 90 gün</option>
          </select>
        }
      />

      <div className="flex items-center gap-1 border-b border-gray-200 mb-5 overflow-x-auto">
        {TABS.map((t) => {
          const Icon = t.icon;
          return (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              data-testid={`analytics-tab-${t.id}`}
              className={`flex items-center gap-1.5 px-4 py-2 text-sm font-semibold whitespace-nowrap border-b-2 -mb-[2px] transition-colors ${
                tab === t.id
                  ? "border-[#e11d2a] text-[#e11d2a]"
                  : "border-transparent text-gray-600 hover:text-gray-900"
              }`}
            >
              <Icon className="w-4 h-4" /> {t.label}
            </button>
          );
        })}
      </div>

      {tab === "overview" && <OverviewPanel />}
      {tab === "traffic" && <TrafficPanel days={days} />}
      {tab === "users" && <UsersPanel days={days} />}
      {tab === "content" && <ContentPanel days={days} />}
      {tab === "columnists" && <ColumnistsPanel days={days} />}
      {tab === "push" && <PushPanel days={days} />}
      {tab === "ads" && <AdsPanel days={days} />}
      {tab === "yearly" && <YearlyPanel />}
      {tab === "system" && <SystemPanel />}
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* Reusable UI                                                                 */
/* -------------------------------------------------------------------------- */

function Stat({ icon, label, value, sub, color = "default", testId }) {
  const palettes = {
    default: "border-gray-100 text-gray-700",
    red: "border-[#e11d2a]/30 text-[#e11d2a]",
    green: "border-emerald-200 text-emerald-700",
    blue: "border-blue-200 text-blue-700",
    amber: "border-amber-200 text-amber-700",
  };
  return (
    <div
      data-testid={testId}
      className={`bg-white border rounded-lg p-4 flex items-start gap-3 ${palettes[color] || palettes.default}`}
    >
      <div className="p-2 rounded bg-gray-50">{icon}</div>
      <div>
        <div className="text-2xl font-black leading-tight">{value ?? "—"}</div>
        <div className="text-[11px] uppercase tracking-wide text-gray-500 mt-0.5">{label}</div>
        {sub && <div className="text-xs text-gray-400 mt-1">{sub}</div>}
      </div>
    </div>
  );
}

function Section({ title, children, action }) {
  return (
    <section className="bg-white border border-gray-100 rounded-lg p-5 mb-4">
      <div className="flex items-center justify-between mb-3">
        <h3 className="font-black text-lg">{title}</h3>
        {action}
      </div>
      {children}
    </section>
  );
}

function Loading() {
  return <div className="text-sm text-gray-500 py-8 text-center">Yükleniyor…</div>;
}

function useFetch(path, deps = []) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [reloadFlag, setReloadFlag] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    api.get(path).then((r) => {
      if (!cancelled) {
        setData(r.data);
        setLoading(false);
      }
    }).catch(() => {
      if (!cancelled) {
        toast.error("Analitik yüklenemedi");
        setLoading(false);
      }
    });
    return () => { cancelled = true; };
    // eslint-disable-next-line
  }, [...deps, reloadFlag]);

  return { data, loading, reload: () => setReloadFlag((f) => f + 1) };
}

/* -------------------------------------------------------------------------- */
/* Overview                                                                    */
/* -------------------------------------------------------------------------- */

function OverviewPanel() {
  const { data, loading, reload } = useFetch("/analytics/overview", []);
  if (loading) return <Loading />;
  return (
    <div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5">
        <Stat icon={<Eye />} label="Görüntüleme (24h)" value={data.page_views_24h} sub="Son 24 saat" color="red" testId="ov-views-24h" />
        <Stat icon={<Eye />} label="Görüntüleme (7g)" value={data.page_views_7d} />
        <Stat icon={<Newspaper />} label="Yeni Haber (24h)" value={data.news_24h} sub={`7g: ${data.news_7d} • 30g: ${data.news_30d}`} color="blue" />
        <Stat icon={<UsersI />} label="Toplam Kullanıcı" value={data.users_total} sub={`Yeni 7g: ${data.users_7d}`} color="green" />
        <Stat icon={<BellRing />} label="Aktif Push Abone" value={data.push_subs} sub={`Son 7g yayın: ${data.push_sends_7d}`} color="red" />
        <Stat icon={<Inbox />} label="Etkinlik Bekleyen" value={data.events_pending} color="amber" />
        <Stat icon={<Inbox />} label="Şehir Reh. Bekleyen" value={data.cityguide_pending} color="amber" />
        <Stat icon={<Inbox />} label="Haber İpucu Bekleyen" value={data.tips_pending} color="amber" />
        <Stat icon={<Mail />} label="İletişim Formu (7g)" value={data.contact_submissions_7d} />
        <Stat icon={<AlertTriangle />} label="Hata (24h)" value={data.errors_24h} color={data.errors_24h ? "red" : "green"} />
      </div>

      <Section
        title="Yenile"
        action={
          <button onClick={reload} className="text-sm px-3 py-1.5 border border-gray-200 rounded-md flex items-center gap-1 hover:bg-gray-50">
            <RefreshCw className="w-4 h-4" /> Yenile
          </button>
        }
      >
        <p className="text-xs text-gray-500">
          Yeni sayım almak için sağ üst köşedeki Yenile butonuna tıklayın. Bu panel; yeni haberler, kullanıcılar, push aboneleri, onay kuyrukları ve sistem hataları üzerinden hızlı bir özet sunar.
        </p>
      </Section>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* Traffic                                                                     */
/* -------------------------------------------------------------------------- */

function TrafficPanel({ days }) {
  const { data, loading } = useFetch(`/analytics/traffic?days=${days}&limit=10`, [days]);
  if (loading) return <Loading />;

  const series = data.page_views_series.map((d, i) => ({
    date: trDate(d.date),
    views: d.count,
    newsCreated: data.news_created_series[i]?.count || 0,
  }));

  return (
    <div>
      <Section title="Günlük Görüntülenme & Yeni Haber">
        <ResponsiveContainer width="100%" height={260}>
          <AreaChart data={series}>
            <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
            <XAxis dataKey="date" fontSize={11} />
            <YAxis fontSize={11} />
            <Tooltip />
            <Legend />
            <Area type="monotone" dataKey="views" name="Görüntüleme" stroke="#e11d2a" fill="#e11d2a" fillOpacity={0.25} />
            <Area type="monotone" dataKey="newsCreated" name="Yeni Haber" stroke="#1e40af" fill="#1e40af" fillOpacity={0.18} />
          </AreaChart>
        </ResponsiveContainer>
      </Section>

      <div className="grid lg:grid-cols-2 gap-4 mb-4">
        <Section title="En Çok Okunan Haberler">
          <ol className="space-y-2 text-sm" data-testid="top-news-list">
            {data.top_news.map((n, i) => (
              <li key={n.id} className="flex items-center justify-between gap-2 py-1 border-b last:border-0">
                <div className="flex items-start gap-2 min-w-0">
                  <span className="font-mono text-xs text-gray-400 shrink-0 mt-0.5">#{i + 1}</span>
                  <span className="truncate">{n.title}</span>
                </div>
                <span className="text-xs font-bold text-[#e11d2a] shrink-0">{n.views ?? 0}</span>
              </li>
            ))}
            {data.top_news.length === 0 && <li className="text-gray-400 text-xs">Veri yok</li>}
          </ol>
        </Section>

        <Section title="Kategori Dağılımı (Toplam Görüntüleme)">
          <ResponsiveContainer width="100%" height={240}>
            <PieChart>
              <Pie
                data={data.by_category.slice(0, 8)}
                dataKey="views"
                nameKey="name"
                cx="50%"
                cy="50%"
                outerRadius={88}
                label={(e) => e.name}
              >
                {data.by_category.slice(0, 8).map((_, idx) => (
                  <Cell key={idx} fill={COLORS[idx % COLORS.length]} />
                ))}
              </Pie>
              <Tooltip />
            </PieChart>
          </ResponsiveContainer>
        </Section>
      </div>

      <Section title="İl Bazında Trafik (Top 25)">
        <ResponsiveContainer width="100%" height={Math.min(360, Math.max(180, data.by_province.length * 18))}>
          <BarChart data={data.by_province.slice(0, 25)} layout="vertical" margin={{ left: 80 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
            <XAxis type="number" fontSize={11} />
            <YAxis dataKey="name" type="category" fontSize={11} width={100} />
            <Tooltip />
            <Bar dataKey="views" fill="#e11d2a" name="Görüntüleme" />
            <Bar dataKey="count" fill="#1e40af" name="Haber Sayısı" />
          </BarChart>
        </ResponsiveContainer>
      </Section>

      <TrafficSourcesSection days={days} />
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* Traffic Sources (referrer analysis)                                        */
/* -------------------------------------------------------------------------- */

function TrafficSourcesSection({ days }) {
  const { data, loading } = useFetch(
    `/analytics/traffic-sources?days=${days}&limit=200`,
    [days]
  );

  if (loading) {
    return (
      <Section title="Trafik Kaynakları">
        <Loading />
      </Section>
    );
  }

  const grouped = data?.grouped || [];
  const raw = data?.raw || [];
  const totalWithRef = data?.total_with_referrer || 0;
  const totalTracked = data?.total_tracked || 0;
  const refPct = totalTracked > 0 ? Math.round((totalWithRef / totalTracked) * 100) : 0;

  return (
    <div data-testid="traffic-sources-wrap">
      <div className="grid lg:grid-cols-2 gap-4 mb-4">
        <Section title="Trafik Kaynakları — Özet">
          <div className="text-xs text-gray-500 mb-2">
            Toplam izlenen görüntüleme: <strong className="text-gray-800">{totalTracked.toLocaleString("tr-TR")}</strong>
            {" · "}Dış kaynaklı: <strong className="text-gray-800">{totalWithRef.toLocaleString("tr-TR")}</strong> (%{refPct})
          </div>
          {grouped.length === 0 ? (
            <div className="text-gray-400 text-xs py-6 text-center">Veri yok</div>
          ) : (
            <ResponsiveContainer width="100%" height={Math.max(220, grouped.length * 36)}>
              <BarChart data={grouped} layout="vertical" margin={{ left: 80 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
                <XAxis type="number" fontSize={11} />
                <YAxis dataKey="source" type="category" fontSize={11} width={100} />
                <Tooltip />
                <Bar dataKey="count" fill="#e11d2a" name="Ziyaret" />
              </BarChart>
            </ResponsiveContainer>
          )}
        </Section>

        <Section title="Trafik Kaynakları — Dağılım">
          {grouped.length === 0 ? (
            <div className="text-gray-400 text-xs py-6 text-center">Veri yok</div>
          ) : (
            <ResponsiveContainer width="100%" height={260}>
              <PieChart>
                <Pie
                  data={grouped}
                  dataKey="count"
                  nameKey="source"
                  cx="50%"
                  cy="50%"
                  outerRadius={92}
                  label={(e) => `${e.source} (${e.count})`}
                >
                  {grouped.map((_, idx) => (
                    <Cell key={idx} fill={COLORS[idx % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          )}
        </Section>
      </div>

      <Section title={`Ham Referrer URL Listesi (${raw.length})`}>
        <p className="text-xs text-gray-500 mb-2">
          Ziyaretçilerin siteye geldiği tam URL'ler. En çok tıklanan üstte.
        </p>
        {raw.length === 0 ? (
          <div className="text-gray-400 text-xs py-6 text-center" data-testid="traffic-sources-raw-empty">
            Henüz dış kaynaklı referrer verisi toplanmadı. Yeni gelen ziyaretçiler burada görünecek.
          </div>
        ) : (
          <div className="overflow-x-auto max-h-[480px] overflow-y-auto border border-gray-100 rounded">
            <table className="w-full text-sm" data-testid="traffic-sources-raw-table">
              <thead className="bg-gray-50 sticky top-0 z-10">
                <tr className="text-left text-xs text-gray-600">
                  <th className="px-3 py-2 w-8">#</th>
                  <th className="px-3 py-2">Referrer URL</th>
                  <th className="px-3 py-2 w-28">Kaynak</th>
                  <th className="px-3 py-2 w-20 text-right">Ziyaret</th>
                </tr>
              </thead>
              <tbody>
                {raw.map((row, i) => (
                  <tr key={row.referrer} className="border-t border-gray-100 hover:bg-gray-50">
                    <td className="px-3 py-2 text-xs text-gray-400 font-mono">{i + 1}</td>
                    <td className="px-3 py-2 min-w-0">
                      <a
                        href={row.referrer}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-blue-700 hover:underline break-all text-xs"
                        title={row.referrer}
                      >
                        {row.referrer}
                      </a>
                    </td>
                    <td className="px-3 py-2">
                      <span className="inline-block px-2 py-0.5 rounded-full text-[11px] font-semibold bg-gray-100 text-gray-700">
                        {row.source}
                      </span>
                    </td>
                    <td className="px-3 py-2 text-right font-bold text-[#e11d2a]">{row.count}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Section>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* Users                                                                       */
/* -------------------------------------------------------------------------- */

function UsersPanel({ days }) {
  const { data, loading } = useFetch(`/analytics/users?days=${days}`, [days]);
  if (loading) return <Loading />;

  const funnelTotal = data.funnel.registered || 1;
  const funnelData = [
    { stage: "Kayıtlı", value: data.funnel.registered, pct: 100 },
    { stage: "Kaydetti", value: data.funnel.saved, pct: Math.round((data.funnel.saved / funnelTotal) * 100) },
    { stage: "Push Abonesi", value: data.funnel.push_subscribed, pct: Math.round((data.funnel.push_subscribed / funnelTotal) * 100) },
  ];

  return (
    <div>
      <div className="grid grid-cols-3 gap-3 mb-4">
        <Stat icon={<UsersI />} label="DAU" value={data.dau} sub="Aktif kullanıcı (24h)" color="red" />
        <Stat icon={<UsersI />} label="WAU" value={data.wau} sub="Aktif kullanıcı (7g)" color="blue" />
        <Stat icon={<UsersI />} label="MAU" value={data.mau} sub="Aktif kullanıcı (30g)" color="green" />
      </div>

      <Section title="Yeni Kayıt Trendi">
        <ResponsiveContainer width="100%" height={240}>
          <LineChart data={data.registrations_series.map((d) => ({ date: trDate(d.date), count: d.count }))}>
            <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
            <XAxis dataKey="date" fontSize={11} />
            <YAxis fontSize={11} />
            <Tooltip />
            <Line type="monotone" dataKey="count" stroke="#16a34a" strokeWidth={2} dot={false} name="Kayıt" />
          </LineChart>
        </ResponsiveContainer>
      </Section>

      <Section title="Dönüşüm Hunisi">
        <div className="space-y-2">
          {funnelData.map((f, i) => (
            <div key={i}>
              <div className="flex items-center justify-between text-sm mb-1">
                <span className="font-semibold">{f.stage}</span>
                <span className="text-gray-500">
                  {f.value} <span className="text-xs">({f.pct}%)</span>
                </span>
              </div>
              <div className="h-3 bg-gray-100 rounded-full overflow-hidden">
                <div
                  className="h-full rounded-full transition-all"
                  style={{
                    width: `${f.pct}%`,
                    background: `linear-gradient(90deg, ${COLORS[i % COLORS.length]} 0%, ${COLORS[(i + 1) % COLORS.length]} 100%)`,
                  }}
                />
              </div>
            </div>
          ))}
        </div>
      </Section>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* Content                                                                     */
/* -------------------------------------------------------------------------- */

function ContentPanel({ days }) {
  const { data, loading } = useFetch(`/analytics/content?days=${days}&limit=10`, [days]);
  if (loading) return <Loading />;

  const queues = data.queues;

  return (
    <div>
      <Section title="Onay Kuyruğu Yaşlanması (Bekleyen iş başına)">
        <div className="grid md:grid-cols-3 gap-3">
          {[
            { key: "events", label: "Etkinlik Önerileri", color: "amber" },
            { key: "city_guide", label: "Şehir Rehberi", color: "blue" },
            { key: "news_tips", label: "Haber İpuçları", color: "red" },
          ].map((q) => {
            const v = queues[q.key] || { pending: 0, median_hours: 0, max_hours: 0 };
            return (
              <div key={q.key} className="border border-gray-100 rounded-lg p-4">
                <div className="text-sm font-semibold mb-2">{q.label}</div>
                <div className="text-2xl font-black">{v.pending}</div>
                <div className="text-[11px] text-gray-500 uppercase">bekleyen</div>
                <div className="mt-3 grid grid-cols-2 gap-2 text-xs">
                  <div>
                    <div className="text-gray-500">Medyan yaş</div>
                    <div className="font-bold">{v.median_hours} sa</div>
                  </div>
                  <div>
                    <div className="text-gray-500">En eski</div>
                    <div className="font-bold">{v.max_hours} sa</div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </Section>

      <div className="grid lg:grid-cols-2 gap-4">
        <Section title="Yazar Performansı (Haber)">
          <table className="w-full text-sm">
            <thead className="text-left text-xs text-gray-500 uppercase">
              <tr>
                <th className="py-1">Yazar</th>
                <th className="py-1 text-right">Haber</th>
                <th className="py-1 text-right">Toplam Görünt.</th>
                <th className="py-1 text-right">Ortalama</th>
              </tr>
            </thead>
            <tbody>
              {data.top_authors.map((a) => (
                <tr key={a.author_id || a.name} className="border-t">
                  <td className="py-1.5">{a.name}</td>
                  <td className="py-1.5 text-right">{a.news_count}</td>
                  <td className="py-1.5 text-right font-bold text-[#e11d2a]">{a.views_sum}</td>
                  <td className="py-1.5 text-right text-gray-500">{a.avg_views}</td>
                </tr>
              ))}
              {data.top_authors.length === 0 && (
                <tr><td colSpan="4" className="text-center text-gray-400 py-4">Veri yok</td></tr>
              )}
            </tbody>
          </table>
        </Section>

        <Section title="Köşe Yazarı Performansı">
          <table className="w-full text-sm">
            <thead className="text-left text-xs text-gray-500 uppercase">
              <tr>
                <th className="py-1">Köşe Yazarı</th>
                <th className="py-1 text-right">Yazı</th>
                <th className="py-1 text-right">Toplam Görünt.</th>
              </tr>
            </thead>
            <tbody>
              {data.top_columnists.map((c) => (
                <tr key={c.columnist_id || c.name} className="border-t">
                  <td className="py-1.5">{c.name}</td>
                  <td className="py-1.5 text-right">{c.articles}</td>
                  <td className="py-1.5 text-right font-bold text-[#e11d2a]">{c.views_sum}</td>
                </tr>
              ))}
              {data.top_columnists.length === 0 && (
                <tr><td colSpan="3" className="text-center text-gray-400 py-4">Veri yok</td></tr>
              )}
            </tbody>
          </table>
        </Section>
      </div>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* Push                                                                        */
/* -------------------------------------------------------------------------- */

function PushPanel({ days }) {
  const { data, loading } = useFetch(`/analytics/push?days=${days}`, [days]);
  if (loading) return <Loading />;

  const subSeries = data.subscriptions_created_series.map((d, i) => ({
    date: trDate(d.date),
    created: d.count,
    revoked: data.subscriptions_revoked_series[i]?.count || 0,
  }));

  return (
    <div>
      <div className="grid grid-cols-3 gap-3 mb-4">
        <Stat icon={<BellRing />} label="İletilen" value={data.totals.delivered} color="blue" />
        <Stat icon={<TrendingUp />} label="Tıklama" value={data.totals.clicks} color="green" />
        <Stat icon={<Activity />} label="Ortalama CTR" value={`${data.totals.avg_ctr}%`} color="red" />
      </div>

      <Section title="Abone Trendi (Yeni vs İptal)">
        <ResponsiveContainer width="100%" height={240}>
          <LineChart data={subSeries}>
            <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
            <XAxis dataKey="date" fontSize={11} />
            <YAxis fontSize={11} />
            <Tooltip />
            <Legend />
            <Line type="monotone" dataKey="created" stroke="#16a34a" strokeWidth={2} dot={false} name="Yeni" />
            <Line type="monotone" dataKey="revoked" stroke="#e11d2a" strokeWidth={2} dot={false} name="İptal" />
          </LineChart>
        </ResponsiveContainer>
      </Section>

      <div className="grid lg:grid-cols-2 gap-4">
        <Section title="Son Yayınlar & CTR">
          <table className="w-full text-sm">
            <thead className="text-left text-xs text-gray-500 uppercase">
              <tr>
                <th className="py-1">Tarih</th>
                <th className="py-1">Başlık</th>
                <th className="py-1 text-right">İletilen</th>
                <th className="py-1 text-right">Tıklama</th>
                <th className="py-1 text-right">CTR</th>
              </tr>
            </thead>
            <tbody>
              {(data.sends || []).slice(0, 15).map((s) => (
                <tr key={s.id} className="border-t">
                  <td className="py-1.5 text-xs whitespace-nowrap text-gray-500">{trDateTime(s.sent_at)}</td>
                  <td className="py-1.5 truncate max-w-[180px]" title={s.title}>{s.title}</td>
                  <td className="py-1.5 text-right">{s.success_count}/{s.total}</td>
                  <td className="py-1.5 text-right">{s.clicks}</td>
                  <td className="py-1.5 text-right font-bold text-[#e11d2a]">{s.ctr}%</td>
                </tr>
              ))}
              {(!data.sends || data.sends.length === 0) && (
                <tr><td colSpan="5" className="text-center text-gray-400 py-4">Yayın yok</td></tr>
              )}
            </tbody>
          </table>
        </Section>

        <Section title="İl Bazında Aboneler (Aktif)">
          <div className="max-h-72 overflow-y-auto">
            <table className="w-full text-sm">
              <tbody>
                {data.by_province.map((row, i) => (
                  <tr key={i} className="border-t">
                    <td className="py-1.5">{row.name}</td>
                    <td className="py-1.5 text-right font-mono font-bold">{row.count}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Section>
      </div>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* Ads                                                                         */
/* -------------------------------------------------------------------------- */

function AdsPanel({ days }) {
  const { data, loading } = useFetch(`/analytics/ads?days=${days}`, [days]);
  if (loading) return <Loading />;

  const series = data.impressions_series.map((d, i) => ({
    date: trDate(d.date),
    impressions: d.count,
    clicks: data.clicks_series[i]?.count || 0,
  }));

  return (
    <div>
      <div className="grid grid-cols-3 gap-3 mb-4">
        <Stat icon={<Eye />} label="Toplam Gösterim" value={series.reduce((a, b) => a + b.impressions, 0)} color="blue" />
        <Stat icon={<Activity />} label="Toplam Tıklama" value={series.reduce((a, b) => a + b.clicks, 0)} color="green" />
        <Stat icon={<Megaphone />} label="Aktif Kampanya" value={data.active_campaigns} color="red" />
      </div>

      <Section title="Gösterim & Tıklama Trendi">
        <ResponsiveContainer width="100%" height={240}>
          <LineChart data={series}>
            <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
            <XAxis dataKey="date" fontSize={11} />
            <YAxis fontSize={11} />
            <Tooltip />
            <Legend />
            <Line type="monotone" dataKey="impressions" stroke="#1e40af" strokeWidth={2} dot={false} name="Gösterim" />
            <Line type="monotone" dataKey="clicks" stroke="#e11d2a" strokeWidth={2} dot={false} name="Tıklama" />
          </LineChart>
        </ResponsiveContainer>
      </Section>

      <Section title="Alan Bazında Performans">
        <table className="w-full text-sm">
          <thead className="text-left text-xs text-gray-500 uppercase">
            <tr>
              <th className="py-1">Alan</th>
              <th className="py-1">Pozisyon</th>
              <th className="py-1 text-right">Gösterim</th>
              <th className="py-1 text-right">Tıklama</th>
              <th className="py-1 text-right">CTR</th>
            </tr>
          </thead>
          <tbody>
            {data.by_zone.map((z) => (
              <tr key={z.zone_id} className="border-t">
                <td className="py-1.5">{z.name}</td>
                <td className="py-1.5 text-gray-500 text-xs">{z.position}</td>
                <td className="py-1.5 text-right">{z.impressions}</td>
                <td className="py-1.5 text-right">{z.clicks}</td>
                <td className="py-1.5 text-right font-bold text-[#e11d2a]">{z.ctr}%</td>
              </tr>
            ))}
            {data.by_zone.length === 0 && (
              <tr><td colSpan="5" className="text-center text-gray-400 py-6">Reklam verisi yok</td></tr>
            )}
          </tbody>
        </table>
      </Section>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* Yearly report                                                               */
/* -------------------------------------------------------------------------- */

function YoyBadge({ pct, testId }) {
  if (pct === null || pct === undefined) {
    return (
      <span
        data-testid={testId}
        className="inline-flex items-center gap-0.5 text-[11px] font-semibold text-gray-400"
      >
        —
      </span>
    );
  }
  const up = pct >= 0;
  return (
    <span
      data-testid={testId}
      className={`inline-flex items-center gap-0.5 text-[11px] font-semibold ${
        up ? "text-emerald-600" : "text-[#e11d2a]"
      }`}
    >
      {up ? <ArrowUpRight className="w-3 h-3" /> : <ArrowDownRight className="w-3 h-3" />}
      {up ? "+" : ""}{pct}%
    </span>
  );
}

function YearlyPanel() {
  const currentYear = new Date().getFullYear();
  const [year, setYear] = useState(currentYear);
  const { data, loading, reload } = useFetch(`/analytics/yearly?year=${year}&limit=10`, [year]);

  const years = useMemo(() => {
    const arr = [];
    for (let y = currentYear; y >= currentYear - 4; y--) arr.push(y);
    return arr;
  }, [currentYear]);

  if (loading) return <Loading />;
  if (!data) return null;

  const totals = data.totals || { current: {}, previous: {}, yoy_pct: {} };
  const best = data.best_month || {};

  return (
    <div data-testid="yearly-panel">
      <Section
        title={`${year} Yıllık Rapor`}
        action={
          <div className="flex items-center gap-2">
            <select
              value={year}
              onChange={(e) => setYear(parseInt(e.target.value, 10))}
              data-testid="yearly-year-select"
              className="text-sm px-3 py-1.5 border border-gray-200 rounded-md"
            >
              {years.map((y) => (
                <option key={y} value={y}>{y}</option>
              ))}
            </select>
            <button
              type="button"
              onClick={reload}
              data-testid="yearly-refresh"
              className="text-sm px-3 py-1.5 border border-gray-200 rounded-md flex items-center gap-1 hover:bg-gray-50"
            >
              <RefreshCw className="w-4 h-4" /> Yenile
            </button>
          </div>
        }
      >
        <p className="text-xs text-gray-500">
          {year} yılının tüm ayları için trafik, içerik ve kullanıcı verileri;
          {" "}{data.previous_year} yılı ile karşılaştırılarak (YoY) sunulur.
        </p>
      </Section>

      {/* Totals with YoY badges */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
        <div className="bg-white border rounded-lg p-4" data-testid="yearly-total-views">
          <div className="flex items-center justify-between">
            <div className="p-2 rounded bg-gray-50"><Eye className="w-5 h-5 text-[#e11d2a]" /></div>
            <YoyBadge pct={totals.yoy_pct?.page_views} testId="yoy-views" />
          </div>
          <div className="text-2xl font-black mt-2">{totals.current?.page_views ?? 0}</div>
          <div className="text-[11px] uppercase tracking-wide text-gray-500 mt-0.5">Görüntüleme</div>
          <div className="text-xs text-gray-400 mt-1">Geçen yıl: {totals.previous?.page_views ?? 0}</div>
        </div>

        <div className="bg-white border rounded-lg p-4" data-testid="yearly-total-news">
          <div className="flex items-center justify-between">
            <div className="p-2 rounded bg-gray-50"><Newspaper className="w-5 h-5 text-blue-700" /></div>
            <YoyBadge pct={totals.yoy_pct?.news} testId="yoy-news" />
          </div>
          <div className="text-2xl font-black mt-2">{totals.current?.news ?? 0}</div>
          <div className="text-[11px] uppercase tracking-wide text-gray-500 mt-0.5">Yayınlanan Haber</div>
          <div className="text-xs text-gray-400 mt-1">Geçen yıl: {totals.previous?.news ?? 0}</div>
        </div>

        <div className="bg-white border rounded-lg p-4" data-testid="yearly-total-users">
          <div className="flex items-center justify-between">
            <div className="p-2 rounded bg-gray-50"><UsersI className="w-5 h-5 text-emerald-700" /></div>
            <YoyBadge pct={totals.yoy_pct?.users} testId="yoy-users" />
          </div>
          <div className="text-2xl font-black mt-2">{totals.current?.users ?? 0}</div>
          <div className="text-[11px] uppercase tracking-wide text-gray-500 mt-0.5">Yeni Kayıt</div>
          <div className="text-xs text-gray-400 mt-1">Geçen yıl: {totals.previous?.users ?? 0}</div>
        </div>

        <div className="bg-white border rounded-lg p-4" data-testid="yearly-total-push">
          <div className="flex items-center justify-between">
            <div className="p-2 rounded bg-gray-50"><BellRing className="w-5 h-5 text-amber-600" /></div>
            <YoyBadge pct={totals.yoy_pct?.push_sends} testId="yoy-push" />
          </div>
          <div className="text-2xl font-black mt-2">{totals.current?.push_sends ?? 0}</div>
          <div className="text-[11px] uppercase tracking-wide text-gray-500 mt-0.5">Push Yayını</div>
          <div className="text-xs text-gray-400 mt-1">Geçen yıl: {totals.previous?.push_sends ?? 0}</div>
        </div>
      </div>

      {/* Best month highlights */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-4">
        {[
          { key: "page_views", label: "Zirve Trafik Ayı", icon: Eye, color: "text-[#e11d2a]" },
          { key: "news", label: "En Fazla Haber Ayı", icon: Newspaper, color: "text-blue-700" },
          { key: "users", label: "Zirve Kayıt Ayı", icon: UsersI, color: "text-emerald-700" },
        ].map((b) => {
          const rec = best[b.key] || { month: "—", value: 0 };
          const Icon = b.icon;
          return (
            <div key={b.key} className="bg-white border border-gray-100 rounded-lg p-4 flex items-center gap-3" data-testid={`yearly-best-${b.key}`}>
              <div className="p-2 rounded bg-gray-50"><Trophy className={`w-5 h-5 ${b.color}`} /></div>
              <div>
                <div className="text-[11px] uppercase tracking-wide text-gray-500">{b.label}</div>
                <div className="text-lg font-black flex items-center gap-2">
                  {rec.month || "—"}
                  <span className="text-xs font-semibold text-gray-500">({rec.value})</span>
                </div>
                <div className="text-[11px] text-gray-400 flex items-center gap-1 mt-0.5">
                  <Icon className={`w-3 h-3 ${b.color}`} /> {year} yılının en yüksek ayı
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Monthly page views comparison */}
      <Section title={`Aylık Görüntüleme — ${year} vs ${data.previous_year}`}>
        <ResponsiveContainer width="100%" height={280}>
          <BarChart data={data.series?.page_views || []} data-testid="yearly-chart-views">
            <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
            <XAxis dataKey="month" fontSize={11} />
            <YAxis fontSize={11} />
            <Tooltip />
            <Legend />
            <Bar dataKey="previous" name={`${data.previous_year}`} fill="#cbd5e1" radius={[3, 3, 0, 0]} />
            <Bar dataKey="current" name={`${year}`} fill="#e11d2a" radius={[3, 3, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </Section>

      <div className="grid lg:grid-cols-2 gap-4 mb-4">
        <Section title={`Aylık Haber — ${year} vs ${data.previous_year}`}>
          <ResponsiveContainer width="100%" height={220}>
            <LineChart data={data.series?.news_created || []}>
              <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
              <XAxis dataKey="month" fontSize={11} />
              <YAxis fontSize={11} />
              <Tooltip />
              <Legend />
              <Line type="monotone" dataKey="previous" name={`${data.previous_year}`} stroke="#94a3b8" strokeWidth={2} dot={false} />
              <Line type="monotone" dataKey="current" name={`${year}`} stroke="#1e40af" strokeWidth={2} />
            </LineChart>
          </ResponsiveContainer>
        </Section>

        <Section title={`Aylık Yeni Kayıt — ${year} vs ${data.previous_year}`}>
          <ResponsiveContainer width="100%" height={220}>
            <LineChart data={data.series?.users_registered || []}>
              <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
              <XAxis dataKey="month" fontSize={11} />
              <YAxis fontSize={11} />
              <Tooltip />
              <Legend />
              <Line type="monotone" dataKey="previous" name={`${data.previous_year}`} stroke="#94a3b8" strokeWidth={2} dot={false} />
              <Line type="monotone" dataKey="current" name={`${year}`} stroke="#16a34a" strokeWidth={2} />
            </LineChart>
          </ResponsiveContainer>
        </Section>
      </div>

      {/* Top news of the year */}
      <div className="grid lg:grid-cols-2 gap-4">
        <Section title={`${year} Yılının En Çok Okunan Haberleri`}>
          <ol className="space-y-2 text-sm" data-testid="yearly-top-news">
            {(data.top_news || []).map((n, i) => (
              <li key={n.id} className="flex items-center justify-between gap-2 py-1 border-b last:border-0">
                <div className="flex items-start gap-2 min-w-0">
                  <span className="font-mono text-xs text-gray-400 shrink-0 mt-0.5">#{i + 1}</span>
                  <span className="truncate">{n.title}</span>
                </div>
                <span className="text-xs font-bold text-[#e11d2a] shrink-0">{n.views ?? 0}</span>
              </li>
            ))}
            {(!data.top_news || data.top_news.length === 0) && (
              <li className="text-gray-400 text-xs">Bu yıla ait haber verisi yok</li>
            )}
          </ol>
        </Section>

        <Section title={`${year} Yılının En İyi Yazarları`}>
          <table className="w-full text-sm" data-testid="yearly-top-authors">
            <thead className="text-left text-xs text-gray-500 uppercase">
              <tr>
                <th className="py-1">Yazar</th>
                <th className="py-1 text-right">Haber</th>
                <th className="py-1 text-right">Toplam Görünt.</th>
                <th className="py-1 text-right">Ortalama</th>
              </tr>
            </thead>
            <tbody>
              {(data.top_authors || []).map((a) => (
                <tr key={a.author_id || a.name} className="border-t">
                  <td className="py-1.5">{a.name}</td>
                  <td className="py-1.5 text-right">{a.news_count}</td>
                  <td className="py-1.5 text-right font-bold text-[#e11d2a]">{a.views_sum}</td>
                  <td className="py-1.5 text-right text-gray-500">{a.avg_views}</td>
                </tr>
              ))}
              {(!data.top_authors || data.top_authors.length === 0) && (
                <tr><td colSpan="4" className="text-center text-gray-400 py-4">Veri yok</td></tr>
              )}
            </tbody>
          </table>
        </Section>
      </div>

      {/* Province + Category breakdown */}
      <div className="grid lg:grid-cols-2 gap-4 mt-4">
        <Section title={`${year} — İl Bazında Trafik (Top 15)`}>
          <ResponsiveContainer width="100%" height={Math.min(340, Math.max(180, (data.by_province || []).length * 20))}>
            <BarChart data={(data.by_province || []).slice(0, 15)} layout="vertical" margin={{ left: 80 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
              <XAxis type="number" fontSize={11} />
              <YAxis dataKey="name" type="category" fontSize={11} width={100} />
              <Tooltip />
              <Bar dataKey="views" fill="#e11d2a" name="Görüntüleme" />
              <Bar dataKey="count" fill="#1e40af" name="Haber" />
            </BarChart>
          </ResponsiveContainer>
        </Section>

        <Section title={`${year} — Kategori Dağılımı`}>
          <ResponsiveContainer width="100%" height={280}>
            <PieChart>
              <Pie
                data={(data.by_category || []).slice(0, 10)}
                dataKey="views"
                nameKey="name"
                cx="50%"
                cy="50%"
                outerRadius={100}
                label={(e) => e.name}
              >
                {(data.by_category || []).slice(0, 10).map((c, idx) => (
                  <Cell key={idx} fill={c.color || COLORS[idx % COLORS.length]} />
                ))}
              </Pie>
              <Tooltip />
            </PieChart>
          </ResponsiveContainer>
        </Section>
      </div>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* System                                                                      */
/* -------------------------------------------------------------------------- */

function SystemPanel() {
  const { data, loading, reload } = useFetch("/analytics/system", []);
  if (loading) return <Loading />;
  const recent = data.scheduler || [];
  const failed = recent.filter((r) => r.status === "failed").length;

  return (
    <div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
        <Stat
          icon={<Mail />}
          label="E-mail (7g)"
          value={`${data.email.sent_7d}/${data.email.total_7d}`}
          sub={`Başarı: ${data.email.success_rate}%`}
          color={data.email.success_rate >= 90 ? "green" : "amber"}
        />
        <Stat icon={<HardDrive />} label="Medya Depolama" value={fmtBytes(data.storage.bytes)} sub={`${data.storage.count} dosya`} color="blue" />
        <Stat icon={<AlertTriangle />} label="Hata (24h)" value={data.errors_24h} color={data.errors_24h ? "red" : "green"} />
        <Stat icon={<Clock />} label="Scheduler Hatası" value={failed} sub={`Son ${recent.length} çalıştırma`} color={failed ? "red" : "green"} />
      </div>

      <Section
        title="Scheduler Son Çalıştırmalar"
        action={
          <button onClick={reload} className="text-sm px-3 py-1.5 border border-gray-200 rounded-md flex items-center gap-1 hover:bg-gray-50">
            <RefreshCw className="w-4 h-4" /> Yenile
          </button>
        }
      >
        <table className="w-full text-sm">
          <thead className="text-left text-xs text-gray-500 uppercase">
            <tr>
              <th className="py-1">Job</th>
              <th className="py-1">Durum</th>
              <th className="py-1">Süre (ms)</th>
              <th className="py-1">Zaman</th>
              <th className="py-1">Mesaj</th>
            </tr>
          </thead>
          <tbody>
            {recent.map((r, i) => (
              <tr key={i} className="border-t">
                <td className="py-1.5 font-mono text-xs">{r.job}</td>
                <td className="py-1.5">
                  <span className={`text-xs px-1.5 py-0.5 rounded ${r.status === "success" ? "bg-emerald-100 text-emerald-700" : "bg-red-100 text-red-700"}`}>
                    {r.status}
                  </span>
                </td>
                <td className="py-1.5 text-right text-xs">{r.duration_ms}</td>
                <td className="py-1.5 text-xs whitespace-nowrap text-gray-500">{trDateTime(r.ran_at)}</td>
                <td className="py-1.5 text-xs text-gray-600 max-w-md truncate" title={r.message || ""}>{r.message}</td>
              </tr>
            ))}
            {recent.length === 0 && (
              <tr><td colSpan="5" className="text-center text-gray-400 py-4">Henüz çalıştırma yok</td></tr>
            )}
          </tbody>
        </table>
      </Section>
    </div>
  );
}


/* -------------------------------------------------------------------------- */
/* Columnists detail panel                                                     */
/* -------------------------------------------------------------------------- */

function ColumnistsPanel({ days }) {
  const { data, loading } = useFetch(`/analytics/columnists?days=${days}&limit=100`, [days]);
  const [sortBy, setSortBy] = useState("total_clicks");
  const [expanded, setExpanded] = useState(null);

  if (loading || !data) return <Loading />;
  const rows = [...(data.columnists || [])].sort((a, b) => (b[sortBy] || 0) - (a[sortBy] || 0));

  const sortHeader = (field, label) => (
    <th
      className={`py-2 pr-4 cursor-pointer select-none ${sortBy === field ? "text-[#e11d2a]" : ""}`}
      onClick={() => setSortBy(field)}
      data-testid={`analytics-cols-sort-${field}`}
    >
      {label} {sortBy === field ? "▾" : ""}
    </th>
  );

  const totalProfile = rows.reduce((s, r) => s + (r.profile_views || 0), 0);
  const totalArticle = rows.reduce((s, r) => s + (r.article_views || 0), 0);

  return (
    <div data-testid="analytics-columnists-panel">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-4">
        <Stat
          icon={<UsersI className="w-5 h-5" />}
          label="Toplam Köşe Yazarı"
          value={rows.length}
          sub={`Son ${days} gün`}
          testId="analytics-cols-count"
        />
        <Stat
          icon={<TrendingUp className="w-5 h-5" />}
          label="Profil Görüntüleme"
          value={totalProfile.toLocaleString("tr-TR")}
          sub="/kose-yazilari/* sayfaları"
          color="blue"
          testId="analytics-cols-profile-views"
        />
        <Stat
          icon={<Newspaper className="w-5 h-5" />}
          label="Yazı Tıklaması"
          value={totalArticle.toLocaleString("tr-TR")}
          sub="/yazi/* sayfaları"
          color="red"
          testId="analytics-cols-article-views"
        />
      </div>

      <Section title={`Köşe Yazarları Detay (Son ${days} gün)`}>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-left text-xs uppercase text-gray-500">
              <tr>
                <th className="py-2 pr-4">Köşe Yazarı</th>
                {sortHeader("profile_views", "Profil")}
                {sortHeader("article_views", "Yazı Tıklama")}
                {sortHeader("articles_count", "Yazı Sayısı")}
                {sortHeader("total_clicks", "Toplam Tıklama")}
                <th className="py-2 pr-4"></th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <React.Fragment key={r.columnist_id}>
                  <tr className="border-t hover:bg-gray-50" data-testid={`analytics-cols-row-${r.columnist_id}`}>
                    <td className="py-2 pr-4">
                      <div className="flex items-center gap-2">
                        {r.avatar ? (
                          <img src={r.avatar} alt="" className="w-7 h-7 rounded-full object-cover" />
                        ) : (
                          <div className="w-7 h-7 rounded-full bg-gray-200 flex items-center justify-center text-[10px] text-gray-600">
                            {(r.name || "?").slice(0, 1)}
                          </div>
                        )}
                        <span className="font-semibold">{r.name || "—"}</span>
                      </div>
                    </td>
                    <td className="py-2 pr-4 tabular-nums">{(r.profile_views || 0).toLocaleString("tr-TR")}</td>
                    <td className="py-2 pr-4 tabular-nums">{(r.article_views || 0).toLocaleString("tr-TR")}</td>
                    <td className="py-2 pr-4 tabular-nums">{r.articles_count || 0}</td>
                    <td className="py-2 pr-4 tabular-nums font-black">{(r.total_clicks || 0).toLocaleString("tr-TR")}</td>
                    <td className="py-2 pr-4 text-right">
                      {r.top_articles && r.top_articles.length > 0 && (
                        <button
                          type="button"
                          onClick={() => setExpanded(expanded === r.columnist_id ? null : r.columnist_id)}
                          className="text-xs text-blue-600 hover:underline"
                          data-testid={`analytics-cols-expand-${r.columnist_id}`}
                        >
                          {expanded === r.columnist_id ? "Gizle" : `En çok okunan ${r.top_articles.length} yazı`}
                        </button>
                      )}
                    </td>
                  </tr>
                  {expanded === r.columnist_id && r.top_articles && r.top_articles.length > 0 && (
                    <tr className="bg-amber-50/50 border-t">
                      <td colSpan="6" className="py-3 px-4">
                        <div className="text-xs font-semibold text-gray-700 mb-2">En çok okunan yazılar:</div>
                        <ul className="space-y-1">
                          {r.top_articles.map((a) => (
                            <li key={a.id} className="text-xs flex items-center justify-between gap-2">
                              <span className="truncate text-gray-800">• {a.title}</span>
                              <span className="tabular-nums text-[#e11d2a] font-bold shrink-0">
                                {a.views.toLocaleString("tr-TR")} tıklama
                              </span>
                            </li>
                          ))}
                        </ul>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
              {rows.length === 0 && (
                <tr><td colSpan="6" className="text-center text-gray-400 py-6">Bu dönemde köşe yazarı aktivitesi yok</td></tr>
              )}
            </tbody>
          </table>
        </div>
        <p className="mt-3 text-[11px] text-gray-500">
          Profil = <code>/kose-yazilari/&lt;slug&gt;</code> sayfası ziyaretleri. Yazı Tıklama = yazarın her bir yazısına yapılan tıklamaların toplamı. İki metrik tamamen ayrıdır.
        </p>
      </Section>
    </div>
  );
}
